import React from 'react';
import { useAdminDashboard } from '../../hooks/useAdminDashboard';
import { StatCard, UserTierStats } from './StatCard';
import { UserGrowthChart, SubscriptionPieChart } from './Charts';
import { RecentUsersCard } from './RecentActivity';
import { 
  Users, 
  FolderOpen, 
  FileText, 
  CheckSquare, 
  Crown, 
  UserCheck,
  Loader2,
  AlertCircle,
  RefreshCw
} from 'lucide-react';

export default function AdminDashboard() {
  const { data, loading, error, refetch } = useAdminDashboard();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-600 mb-4">Error loading dashboard: {error}</p>
          <button
            onClick={refetch}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-4 space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center pb-4 border-b">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
          <p className="text-sm text-gray-600">Platform metrics and user activity</p>
        </div>
        <button
          onClick={refetch}
          className="inline-flex items-center px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </button>
      </div>

      {/* Key Metrics - Compact Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard
          title="Users"
          value={data.totalUsers}
          icon={Users}
          color="blue"
          compact={true}
        />
        <StatCard
          title="Groups"
          value={data.totalProjectGroups}
          icon={FolderOpen}
          color="green"
          compact={true}
        />
        <StatCard
          title="Projects"
          value={data.totalProjects}
          icon={FileText}
          color="purple"
          compact={true}
        />
        <StatCard
          title="Tasks"
          value={data.totalTasks}
          icon={CheckSquare}
          color="yellow"
          compact={true}
        />
      </div>

      {/* Subscription Breakdown - Compact Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <UserTierStats
          title="Free Users"
          users={data.freeUsers}
          projectGroups={data.freeUserStats.projectGroups}
          projects={data.freeUserStats.projects}
          tasks={data.freeUserStats.tasks}
          color="red"
          compact={true}
        />
        <UserTierStats
          title="Pro Users" 
          users={data.proUsers}
          projectGroups={data.proUserStats.projectGroups}
          projects={data.proUserStats.projects}
          tasks={data.proUserStats.tasks}
          color="green"
          compact={true}
        />
        <div className="bg-white rounded-lg border p-4">
          <h3 className="text-sm font-medium text-gray-900 mb-3">User Subscription Breakdown</h3>
          <SubscriptionPieChart data={data.subscriptionBreakdown} compact={true} />
        </div>
      </div>

      {/* Charts and Activity - Side by Side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div className="bg-white rounded-lg border p-4">
          <h3 className="text-sm font-medium text-gray-900 mb-3">User Growth</h3>
          <UserGrowthChart data={data.userGrowthData} compact={true} />
        </div>
        <RecentUsersCard users={data.recentUsers} compact={true} />
      </div>

      {/* Summary Stats - Single Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard
          title="Free Users"
          value={data.freeUsers}
          icon={UserCheck}
          color="red"
          compact={true}
        />
        <StatCard
          title="Pro Users"
          value={data.proUsers}
          icon={Crown}
          color="yellow"
          compact={true}
        />
        <StatCard
          title="Conversion"
          value={data.totalUsers > 0 ? `${((data.proUsers / data.totalUsers) * 100).toFixed(1)}%` : '0%'}
          icon={Users}
          color="indigo"
          compact={true}
        />
        <StatCard
          title="Avg Tasks"
          value={data.totalUsers > 0 ? (data.totalTasks / data.totalUsers).toFixed(1) : '0'}
          icon={CheckSquare}
          color="purple"
          compact={true}
        />
      </div>
    </div>
  );
}
